/**
 * ProactiveSuggestionsBanner - Displays contextual AI suggestions
 * Part of Support AI P2 - Proactive Suggestions
 * 
 * Trigger contexts (throttled, non-blocking):
 * - No Acceptance/KPIs saved → suggest "Add Acceptance & KPIs"
 * - Decision Approved without benefit/KPI linkage → suggest "Link Benefits/KPIs"
 * - Drift detected on latest baseline → suggest "Propose Baseline Update"
 * - ≥2 High Risks on same Epic → suggest "Review Risks" / "Escalate to PMO"
 * - NFR/Ops empty with >N features → suggest "Add NFR/Ops template"
 * - Checkpoint overdue (≥30 days) → suggest "Run Checkpoint Summary"
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, X, ChevronRight, CheckCircle, Target, 
  AlertTriangle, GitBranch, FileText, Lightbulb, Clock,
  ArrowRight, ExternalLink, Ban
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';

// Icon mapping for suggestion types
const SUGGESTION_ICONS = {
  acceptance: CheckCircle,
  benefits: Target,
  kpis: Target,
  baseline: FileText,
  escalation: AlertTriangle,
  governance: GitBranch,
  template: FileText,
  checkpoint: Clock,
};

// Priority colors with better contrast
const PRIORITY_COLORS = {
  high: 'border-l-4 border-l-red-500 bg-gradient-to-r from-red-50 to-white',
  medium: 'border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-white',
  low: 'border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50 to-white',
};

// Throttle interval for API calls (5 minutes)
const THROTTLE_INTERVAL = 5 * 60 * 1000;

/**
 * Individual Suggestion Item
 */
const SuggestionItem = ({ suggestion, onAccept, onDismiss, onDefer }) => {
  const IconComponent = SUGGESTION_ICONS[suggestion.type] || Lightbulb;
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`flex items-start gap-3 p-3 rounded-lg transition-all ${PRIORITY_COLORS[suggestion.priority] || PRIORITY_COLORS.medium}`}
      data-testid={`proactive-suggestion-${suggestion.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="alert"
      aria-live="polite"
    >
      <div className="flex-shrink-0 mt-0.5">
        <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center">
          <IconComponent className="w-4 h-4 text-purple-600" />
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium text-sm text-gray-900">{suggestion.title}</h4>
          {suggestion.priority === 'high' && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded animate-pulse">
              High Priority
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 line-clamp-2">{suggestion.description}</p>
        
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => onAccept(suggestion)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
            data-testid={`accept-suggestion-${suggestion.id}`}
          >
            {suggestion.actionLabel || 'Take Action'}
            <ChevronRight className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDefer(suggestion)}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-white/80 rounded-lg transition-colors"
            data-testid={`defer-suggestion-${suggestion.id}`}
          >
            Later
          </button>
          {suggestion.dismissable !== false && (
            <button
              onClick={() => onDismiss(suggestion)}
              className={`p-1.5 text-gray-400 hover:text-gray-600 rounded transition-all ${
                isHovered ? 'opacity-100' : 'opacity-50'
              }`}
              title="Dismiss"
              aria-label="Dismiss suggestion"
              data-testid={`dismiss-suggestion-${suggestion.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Learn more link if provided */}
        {suggestion.learnMoreUrl && (
          <a
            href={suggestion.learnMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-xs text-purple-600 hover:text-purple-700"
          >
            Learn more
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
};

/**
 * Main ProactiveSuggestionsBanner Component
 */
const ProactiveSuggestionsBanner = ({ 
  context,  // 'scope_extraction', 'decision_approved', 'inspector', 'project_dashboard', etc.
  entityId,
  entityType,
  onNavigate,
  maxSuggestions = 3,
  className = ''
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFeature, scopingTier } = useProductMode();
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  const userId = user?.id;
  
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());
  const [minimized, setMinimized] = useState(false);
  
  // Throttle ref to prevent excessive API calls
  const lastFetchRef = useRef(null);
  const fetchingRef = useRef(false);
  
  // Check if feature is enabled
  const isFeatureEnabled = getFeature('ai.proactiveSuggestions', false);
  
  // Load suggestions with throttling
  const loadSuggestions = useCallback(async (force = false) => {
    if (!tenantId || !context || !isFeatureEnabled) return;
    
    // Throttle check
    const now = Date.now();
    if (!force && lastFetchRef.current && (now - lastFetchRef.current) < THROTTLE_INTERVAL) {
      return;
    }
    
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    
    fetchingRef.current = true;
    setLoading(true);
    
    try {
      const response = await api.getProactiveSuggestions(
        tenantId,
        userId,
        context,
        entityId,
        entityType
      );
      
      lastFetchRef.current = now;
      setSuggestions(response.data.suggestions || []);
      setEnabled(response.data.enabled !== false);
      
      // Track telemetry for each suggestion shown
      (response.data.suggestions || []).forEach(s => {
        trackEvent(TELEMETRY_EVENTS.AI_PROACTIVE_SUGGESTION_SHOWN, {
          key: s.id,
          context
        });
      });
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [tenantId, userId, context, entityId, entityType, isFeatureEnabled]);
  
  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);
  
  // Handle accept - navigate to action route
  const handleAccept = useCallback(async (suggestion) => {
    trackEvent(TELEMETRY_EVENTS.AI_PROACTIVE_SUGGESTION_ACCEPTED, {
      key: suggestion.id
    });
    
    // Submit feedback for learning
    try {
      await api.submitSuggestionFeedback(tenantId, userId, {
        suggestionId: suggestion.id,
        action: 'accepted'
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
    
    // Navigate to action route
    if (suggestion.actionRoute) {
      if (onNavigate) {
        onNavigate(suggestion.actionRoute, suggestion.actionData);
      } else {
        navigate(suggestion.actionRoute);
      }
    }
    
    // Remove from list
    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
  }, [tenantId, userId, onNavigate, navigate]);
  
  // Handle dismiss
  const handleDismiss = useCallback(async (suggestion) => {
    trackEvent(TELEMETRY_EVENTS.AI_PROACTIVE_SUGGESTION_REJECTED, {
      key: suggestion.id
    });
    
    // Submit feedback for learning
    try {
      await api.submitSuggestionFeedback(tenantId, userId, {
        suggestionId: suggestion.id,
        action: 'rejected'
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
    
    // Remove from list and add to dismissed
    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    setDismissed(prev => new Set([...prev, suggestion.id]));
  }, [tenantId, userId]);
  
  // Handle defer
  const handleDefer = useCallback(async (suggestion) => {
    trackEvent(TELEMETRY_EVENTS.AI_PROACTIVE_SUGGESTION_DEFERRED, {
      key: suggestion.id
    });
    
    // Submit feedback for learning
    try {
      await api.submitSuggestionFeedback(tenantId, userId, {
        suggestionId: suggestion.id,
        action: 'deferred'
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
    
    // Remove from list temporarily
    setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
  }, [tenantId, userId]);
  
  // Filter out dismissed suggestions and limit count
  const visibleSuggestions = suggestions
    .filter(s => !dismissed.has(s.id))
    .slice(0, maxSuggestions);
  
  // Don't render if feature disabled, no suggestions, or minimized
  if (!isFeatureEnabled || !enabled || visibleSuggestions.length === 0) {
    return null;
  }
  
  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className={`flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors ${className}`}
        data-testid="proactive-suggestions-minimized"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-xs font-medium">{visibleSuggestions.length} AI Suggestion{visibleSuggestions.length > 1 ? 's' : ''}</span>
        <ArrowRight className="w-3 h-3" />
      </button>
    );
  }
  
  return (
    <div className={`space-y-2 ${className}`} data-testid="proactive-suggestions-banner">
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="text-xs font-medium text-gray-600">AI Suggestions</span>
          {visibleSuggestions.length > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-full">
              {visibleSuggestions.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setMinimized(true)}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
          title="Minimize"
          aria-label="Minimize suggestions"
        >
          <Ban className="w-3.5 h-3.5" />
        </button>
      </div>
      
      {/* Suggestions */}
      <div className="space-y-2">
        {visibleSuggestions.map(suggestion => (
          <SuggestionItem
            key={suggestion.id}
            suggestion={suggestion}
            onAccept={handleAccept}
            onDismiss={handleDismiss}
            onDefer={handleDefer}
          />
        ))}
      </div>
      
      {/* Show more if there are hidden suggestions */}
      {suggestions.length > maxSuggestions && (
        <button
          onClick={() => loadSuggestions(true)}
          className="w-full py-1.5 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
        >
          {suggestions.length - maxSuggestions} more suggestion{suggestions.length - maxSuggestions > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
};

export default ProactiveSuggestionsBanner;
